import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherCopy, FeatherRefreshCw, FeatherX } from "@subframe/core";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useResetUserPassword } from "@/generated/users/users";
import { generateTemporaryPassword } from "@/lib/temporary-password";

import { accountActionErrorMessage } from "./account-action-error";
import { useInvalidateAccount } from "./use-invalidate-account";

const MIN_LENGTH = 8;
const MAX_LENGTH = 128;

interface ResetPasswordDialogProps {
  userId: string;
  displayName: string;
  username: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

async function copyToClipboard(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Couldn't copy — select it and copy it yourself");
  }
}

/**
 * Manager action to set a temporary password, shown once so it can be handed
 * over. Not every account has an email address, so nothing is sent. The user
 * must choose their own password when they next sign in. The contents are kept
 * out of session recordings: a password is on screen.
 */
export function ResetPasswordDialog({
  userId,
  displayName,
  username,
  open,
  onOpenChange,
}: ResetPasswordDialogProps) {
  const [password, setPassword] = useState(generateTemporaryPassword);
  const [savedPassword, setSavedPassword] = useState<string | null>(null);
  const resetPassword = useResetUserPassword();
  const invalidateAccount = useInvalidateAccount();
  const isSaving = resetPassword.isPending;
  const isValid =
    password.length >= MIN_LENGTH && password.length <= MAX_LENGTH;

  function close() {
    if (isSaving) return;
    setPassword(generateTemporaryPassword());
    setSavedPassword(null);
    onOpenChange(false);
  }

  async function handleSave() {
    if (!isValid) return;
    try {
      await resetPassword.mutateAsync({
        id: userId,
        data: { temporaryPassword: password },
      });
      setSavedPassword(password);
      await invalidateAccount(userId);
    } catch (error) {
      toast.error(
        accountActionErrorMessage(
          error,
          `Could not reset ${displayName}'s password`,
        ),
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent
        showCloseButton={false}
        className="ph-no-capture sentry-block max-w-md p-0"
        data-testid="reset-password-dialog"
      >
        <DialogTitle className="sr-only">Reset password</DialogTitle>

        <div className="flex items-start justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {savedPassword ? "Password reset" : "Reset password"}
            </span>
            <span className="text-body font-body text-subtext-color">
              {savedPassword
                ? `Give ${displayName} these details. They'll choose a new password when they next sign in.`
                : `Set a temporary password for ${displayName} and share it with them. They'll be asked to choose a new one when they next sign in.`}
            </span>
          </div>
          <IconButton
            variant="neutral-tertiary"
            size="medium"
            icon={<FeatherX />}
            disabled={isSaving}
            onClick={close}
          />
        </div>

        {savedPassword ? (
          <div className="flex flex-col gap-6 px-6 py-6">
            <dl className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <dt className="text-caption font-caption text-subtext-color">
                  Username
                </dt>
                <dd className="text-body-bold font-body-bold text-default-font break-all">
                  {username}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-caption font-caption text-subtext-color">
                  Temporary password
                </dt>
                <dd
                  className="font-mono text-body-bold text-default-font break-all"
                  data-testid="temporary-password"
                >
                  {savedPassword}
                </dd>
              </div>
            </dl>
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="neutral-secondary"
                size="large"
                icon={<FeatherCopy />}
                onClick={() =>
                  void copyToClipboard(
                    `Username: ${username}\nTemporary password: ${savedPassword}`,
                    "Login details",
                  )
                }
              >
                Copy details
              </Button>
              <Button
                type="button"
                variant="brand-primary"
                size="large"
                onClick={close}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="flex flex-col gap-6 px-6 py-6"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
          >
            <div className="flex items-end gap-2">
              <TextField
                className="h-auto grow"
                size="large"
                label="Temporary password"
                error={!isValid}
                helpText={
                  isValid
                    ? undefined
                    : `${MIN_LENGTH} to ${MAX_LENGTH} characters`
                }
              >
                <TextField.Input
                  autoComplete="off"
                  spellCheck={false}
                  value={password}
                  disabled={isSaving}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </TextField>
              <IconButton
                variant="neutral-secondary"
                size="large"
                icon={<FeatherRefreshCw />}
                aria-label="Generate a new password"
                disabled={isSaving}
                onClick={() => setPassword(generateTemporaryPassword())}
              />
              <IconButton
                variant="neutral-secondary"
                size="large"
                icon={<FeatherCopy />}
                aria-label="Copy password"
                disabled={isSaving || !password}
                onClick={() => void copyToClipboard(password, "Password")}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="neutral-primary"
                size="large"
                disabled={isSaving}
                onClick={close}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand-primary"
                size="large"
                disabled={isSaving || !isValid}
                loading={isSaving}
              >
                Reset password
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
